import { useMemo } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionViewOptions } from "@common/types/views/transactionViews";
import { SplitSiblings } from "@helpers/transactionSplitUtils";
import { FinancialAccount } from "@common/types/financialAccount";
import { Category } from "@common/types/category";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import type { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";

export type { ViewTableMeta };

export type SchedulePickerTemplate = Pick<
    ScheduleTemplateListItem,
    | "id"
    | "name"
    | "group"
    | "entityIds"
    | "hasSpecificDateAnchor"
    | "type"
    | "revenueSubMode"
    | "expenseSubMode"
>;

interface TransactionTableCallbacks {
    calculateSplitSiblings?: (
        transaction: TransactionListItem,
    ) => SplitSiblings;
    onOpenTransactionReview?: (transactionId: number) => void;
    onCreateSchedule?: (options: {
        transactionId: number;
        entityId: number;
    }) => void;
    onRequestLifecycleAction?: (request: ScheduleLifecycleRequest) => void;
}

export type TransactionScheduleCallbacks = Pick<
    TransactionTableCallbacks,
    "onCreateSchedule" | "onRequestLifecycleAction"
>;

/**
 * Extended table meta for the transactions table.
 * Includes hoisted data (financialAccounts, categoriesById)
 * that is shared across all transaction cell components via table.options.meta.
 */
export interface TransactionsTableMeta extends ViewTableMeta<
    TransactionTableCallbacks,
    TransactionViewOptions
> {
    financialAccounts?: FinancialAccount[];
    categoriesById?: Record<string, Category>;
    scheduleTemplates?: ScheduleTemplateListItem[];
    prioritizedScheduleTemplateId?: string;
    prioritizedScheduleTemplate?: SchedulePickerTemplate;
    registerCellActionOpener?: (params: {
        columnId: string;
        rowId: string | number;
        open: () => void;
    }) => () => void;
    openCellAction?: (params: {
        columnId: string;
        rowId: string | number;
    }) => boolean;
    /**
     * When set, the REVIEW column cell uses task-review behaviour: checking
     * whether all of these fields are reviewed for each row, showing a
     * "Review" button or a reviewed checkmark with an undo action.
     */
    reviewTransactionFields?: TransactionReviewField[];
}

export function useViewTableMeta<TCallbacks, TOptions>(table: {
    options: { meta?: object };
}): ViewTableMeta<TCallbacks, TOptions> {
    return useMemo(
        () => (table.options.meta as ViewTableMeta<TCallbacks, TOptions>) || {},
        [table.options.meta],
    );
}

export function useTransactionViewTableMeta(table: {
    options: { meta?: object };
}): ViewTableMeta<TransactionTableCallbacks, TransactionViewOptions> {
    return useViewTableMeta<TransactionTableCallbacks, TransactionViewOptions>(
        table,
    );
}

/**
 * Typed accessor for all transaction-specific table meta fields.
 * Use this in transaction cell components instead of `(table.options.meta as any)`.
 */
export function getTransactionsTableMeta(table: {
    options: { meta?: object };
}): TransactionsTableMeta {
    return (table.options.meta ?? {}) as TransactionsTableMeta;
}
